import type { LucideIcon } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { Card } from './card';
import { Button } from './button';
import { Eyebrow } from './eyebrow';

type CtaAction = { label: string; onClick: () => void };
type CtaLink = { label: string; to: string };

interface EmptyProps {
  /** Ícone Lucide renderizado no centro do glyph. */
  icon: LucideIcon;
  /** Eyebrow acima do título (opcional). */
  eyebrow?: string;
  /** Título principal obrigatório. */
  title: string;
  /** Texto de apoio (opcional). */
  description?: string;
  /**
   * Call-to-action. Duas formas:
   * - `{ label, onClick }` — botão com handler local
   * - `{ label, to }` — link de navegação via TanStack Router
   */
  cta?: CtaAction | CtaLink;
  /** Variante do Card container (padrão: "default"). */
  variant?: 'default' | 'float' | 'soft';
  className?: string;
}

/**
 * Empty — estado vazio padronizado para listas, tabelas e painéis.
 *
 * Uso:
 * ```tsx
 * <Empty
 *   icon={FileX2}
 *   eyebrow="Lançamentos"
 *   title="Nenhum pagamento encontrado"
 *   description="Ainda não há pagamentos neste período. Que tal adicionar o primeiro?"
 *   cta={{ label: 'Novo pagamento', onClick: () => setOpen(true) }}
 * />
 * ```
 */
export function Empty({
  icon: Icon,
  eyebrow,
  title,
  description,
  cta,
  variant = 'default',
  className,
}: EmptyProps) {
  return (
    <Card variant={variant} className={cn('p-10 text-center', className)}>
      {/* Glyph container */}
      <div className="mx-auto grid h-14 w-14 place-items-center rounded-3 bg-card-soft text-fg-muted mb-3">
        <Icon className="h-7 w-7" strokeWidth={1.6} aria-hidden="true" />
      </div>

      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}

      <h3 className="font-display text-[18px] font-semibold tracking-[-0.015em] text-fg mt-2">
        {title}
      </h3>

      {description && (
        <p className="text-sm text-fg-muted mt-2 max-w-md mx-auto">{description}</p>
      )}

      {cta && 'onClick' in cta && (
        <Button variant="primary" onClick={cta.onClick} className="mt-5">
          {cta.label}
        </Button>
      )}

      {cta && 'to' in cta && (
        <Button variant="primary" asChild className="mt-5">
          <Link to={cta.to as never}>{cta.label}</Link>
        </Button>
      )}
    </Card>
  );
}
